import { useEffect, useState } from 'react';

interface UseScrollSpyOptions {
  /**
   * The activation line, as a fraction of viewport height: a section is
   * active once its top rises above this line. (CR-038 removed the old
   * band-top option — the deepest-above-the-line rule has no use for it.)
   */
  readonly bandBottomFraction?: number;
}

/**
 * Watch a list of element ids and return the section the reader is on: the
 * deepest one whose top has risen above 40% of the viewport (the lower edge
 * of the pre-CR-036 IntersectionObserver rootMargin band). Used by HomeRail
 * to highlight the section the reader is on.
 *
 * CR-036 (Frank email 2026-08-31): this hook previously delegated the band
 * to IntersectionObserver's rootMargin. Browsers IGNORE rootMargin when the
 * document is inside a cross-origin iframe (privacy restriction) — and the
 * tool ships inside exactly that (WordPress full-page iframe, DEC-029). The
 * band silently became the full viewport, so after an anchor jump the
 * PREVIOUS section still touched the viewport's top edge, counted as
 * "intersecting", and won the topmost-visible tie-break — Frank's "clicking
 * the 2nd/3rd/4th tab highlights the previous one". Direct loads were fine,
 * which is why every non-iframed check missed it.
 *
 * The hook is now pure geometry — getBoundingClientRect vs the band,
 * recomputed on scroll/resize (rAF-throttled) — so it behaves identically
 * direct and iframed, in every browser. Do NOT rewrite this back onto
 * IntersectionObserver rootMargin; `useScrollSpy.test.ts` pins the contract
 * by deleting the IO global, and `e2e/home-rail-scrollspy.spec.ts` exercises
 * a real cross-origin iframe.
 *
 * CR-038 (2026-09-01): selection rule is "the DEEPEST section whose top has
 * risen above the band's bottom edge" (0.4·viewport by default). "Deepest"
 * is implemented as last-in-supplied-order, so `ids` MUST be in document
 * order (HomeRail's ITEMS match HomePage's section order). The rule
 * tolerates the anchor landing offset: `scroll-padding-top` on `<html>`
 * (globals.css) now offsets anchor jumps below the sticky chrome so the
 * section number isn't covered, which lands the target at ~134px — a
 * first-crossing-the-band rule would see the previous section's tail there
 * on viewports under ~650px and resurrect the CR-036 previous-tab
 * highlight, while this rule only breaks below vh ≈ offset ÷ 0.4 ≈ 335px
 * (unreachable: the rail is lg-only). Two extra rules keep the
 * highlight honest at the page's ends: while no section has reached the band
 * bottom (reader in the hero) the current highlight is kept, and at the very
 * bottom of the page the last section wins even when a tall viewport
 * prevents its top from ever reaching the band.
 *
 * @example
 *   const active = useScrollSpy(['how-it-works', 'methodology']);
 *   // active === 'how-it-works' while the user is reading that section
 */
export function useScrollSpy(
  ids: ReadonlyArray<string>,
  { bandBottomFraction = 0.4 }: UseScrollSpyOptions = {},
): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  // Stable dependency key — callers pass a fresh array literal every render.
  const idsKey = ids.join('|');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const idList = idsKey === '' ? [] : idsKey.split('|');
    if (idList.length === 0) return;

    const compute = (): void => {
      const viewportH = window.innerHeight;
      const bandBottom = viewportH * bandBottomFraction;

      // Pinned at the very bottom of the page: the last section may never
      // reach the band on tall viewports — highlight it anyway.
      const maxScroll = document.documentElement.scrollHeight - viewportH;
      if (maxScroll > 0 && window.scrollY >= maxScroll - 2) {
        for (let i = idList.length - 1; i >= 0; i -= 1) {
          if (document.getElementById(idList[i]!) !== null) {
            setActive(idList[i]!);
            return;
          }
        }
        return;
      }

      // Deepest section whose top has risen above the band bottom (CR-038).
      // Strict inequality: edge-touching is not crossing (CR-036).
      let next: string | null = null;
      for (const id of idList) {
        const el = document.getElementById(id);
        if (el === null) continue;
        if (el.getBoundingClientRect().top < bandBottom) next = id;
      }
      if (next !== null) setActive(next);
      // else: no section has reached the band yet (reader in the hero) —
      // keep the current highlight rather than flickering to nothing.
    };

    let scheduled = false;
    const schedule = (): void => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        compute();
      });
    };

    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [idsKey, bandBottomFraction]);

  return active;
}
